import React, { Component } from 'react';
import PropTypes from 'prop-types';
import Dropzone from 'react-dropzone-701';
import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogActions from '@material-ui/core/DialogActions';
import Button from '@material-ui/core/Button';
import { ThemeProvider } from '@material-ui/core/styles';
import LinearProgress from '@material-ui/core/LinearProgress';
import { withSnackbar } from 'notistack';
import EditImageIcon from '@material-ui/icons/Edit';
import { Image } from 'cloudinary-react';
import Typography from '@material-ui/core/Typography';
import withWidth from '@material-ui/core/withWidth';

import css from './Logo.module.scss';
import handleServerError from '../../../lib/functions/handleServerError';
import { jerseyWatchTheme } from '../../../redux/modules/organizationDuck';
import toJS from '../../../lib/hocs/toJS';
import { homePath } from '../../../lib/constants/links';

class Logo extends Component {
  constructor(props, context) {
    super(props, context);

    this.state = this.initialState();
  }

  initialState = () => ({
    isSaving: false,
    confirmOverwriteLogoDialogOpen: false,
    logoToBeUploaded: null,
    showSpinner: false,
    uploadProgress: 100,
    uploadProgressDialogOpen: false,
  });

  handleConfirmOverwriteLogo = () => {
    if (this.state.showSpinner) return;
    this.setState({
      showUploadProgressDialog: true,
      uploadProgressDialogOpen: true,
      uploadProgress: 0,
    });

    const config = {
      onUploadProgress: progressEvent => {
        const percentCompleted = Math.round(
          (progressEvent.loaded * 100) / progressEvent.total / 2
        );
        this.setState({ uploadProgress: percentCompleted });
      },
    };

    this.props
      .onUpdateOrganizationFile('logo', this.state.logoToBeUploaded, config)
      .then(_res => this.setState(this.initialState()))
      .catch(error => {
        this.setState({
          uploadProgress: 100,
          uploadProgressDialogOpen: false,
          logoToBeUploaded: false,
        });
        handleServerError(error, [], this.props.enqueueSnackbar);
      });
  };

  handleConfirmOverwriteLogoDialogClose = () => {
    this.setState({
      logoToBeUploaded: null,
      confirmOverwriteLogoDialogOpen: false,
    });
  };

  handleLogoDrop = (acceptedFiles, rejectedFiles) => {
    const { enqueueSnackbar, organization } = this.props;

    if (acceptedFiles.length > 1) {
      enqueueSnackbar('Whoops! Only a single image can be uploaded', {
        variant: 'error',
      });
      return;
    }

    if (rejectedFiles.length > 0 || acceptedFiles.length === 0) {
      enqueueSnackbar('Whoops! You must select an image to be uploaded', {
        variant: 'error',
      });
      return;
    }

    if (organization.logo_filename) {
      this.setState({
        logoToBeUploaded: acceptedFiles[0],
        confirmOverwriteLogoDialogOpen: true,
      });
    } else {
      this.setState(
        {
          logoToBeUploaded: acceptedFiles[0],
        },
        () => this.handleConfirmOverwriteLogo()
      );
    }
  };

  renderConfirmOverwriteLogoDialog() {
    return (
      <ThemeProvider theme={jerseyWatchTheme}>
        <Dialog
          open={this.state.confirmOverwriteLogoDialogOpen}
          onClose={() =>
            this.setState({
              logoToBeUploaded: null,
              confirmOverwriteLogoDialogOpen: false,
            })
          }
        >
          <DialogTitle>
            Are you sure you want to replace the existing logo?
          </DialogTitle>
          <DialogActions>
            <Button
              onClick={() =>
                this.setState({
                  logoToBeUploaded: null,
                  confirmOverwriteLogoDialogOpen: false,
                })
              }
            >
              Cancel
            </Button>
            <Button color="primary" onClick={this.handleConfirmOverwriteLogo}>
              Replace
            </Button>
          </DialogActions>
        </Dialog>
      </ThemeProvider>
    );
  }

  renderLogo() {
    const { organization } = this.props;
    return (
      organization.logo_filename && (
        <Image
          publicId={organization.logo_filename}
          width="150"
          height="150"
          crop="scale"
          alt="logo"
          className={`${css.logo}`}
          style={{ borderRadius: '10px' }}
        />
      )
    );
  }

  renderUploadProgressDialog() {
    return (
      <Dialog open={this.state.uploadProgressDialogOpen}>
        <DialogTitle>Upload Progress</DialogTitle>
        <DialogContent>
          <LinearProgress
            variant="determinate"
            value={this.state.uploadProgress}
          />
        </DialogContent>
      </Dialog>
    );
  }

  renderShowLinkableLogo() {
    return <a href={homePath}>{this.renderLogo()}</a>;
  }

  renderShowEditableLogo() {
    return (
      <div>
        <Dropzone
          onDrop={this.handleLogoDrop}
          className={css.editLogoDropzone}
          acceptStyle={{
            border: '1px dashed black',
          }}
          accept="image/*"
        >
          {this.renderLogo()}
          <div className={css.editLogoIconContainer}>
            <EditImageIcon className={css.editLogoIcon} />
          </div>
        </Dropzone>
        {this.renderUploadProgressDialog()}
        {this.renderConfirmOverwriteLogoDialog()}
      </div>
    );
  }

  renderShowEditableMissingLogo() {
    return (
      <div>
        <Dropzone
          onDrop={this.handleLogoDrop}
          className={css.editLogoDropzone}
          accept="image/*"
        >
          <div className={css.editNoLogo}>
            <Typography variant="h4" style={{ color: '#fff' }}>
              UPLOAD LOGO
            </Typography>
          </div>
        </Dropzone>
        {this.renderUploadProgressDialog()}
      </div>
    );
  }

  render() {
    const { organization, isEditingOrganization, width } = this.props;

    if (['xs', 'sm'].includes(width)) return null;

    if (!organization.show_logo) {
      return null;
    } else if (
      organization.logo_filename &&
      (!isEditingOrganization || !organization.can_edit_design)
    ) {
      return this.renderShowLinkableLogo();
    } else if (
      organization.logo_filename &&
      organization.can_edit_design &&
      isEditingOrganization
    ) {
      return this.renderShowEditableLogo();
    } else if (
      !organization.logo_filename &&
      organization.can_edit_design &&
      isEditingOrganization
    ) {
      return this.renderShowEditableMissingLogo();
    }
    return null;
  }
}

Logo.propTypes = {
  enqueueSnackbar: PropTypes.func.isRequired,
  isEditingOrganization: PropTypes.bool.isRequired,
  onUpdateOrganizationFile: PropTypes.func.isRequired,
  organization: PropTypes.object.isRequired,
  width: PropTypes.string.isRequired,
};
Logo.defaultProps = {};

export default toJS(withWidth()(withSnackbar(Logo)));
